import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FiCheckCircle, FiTarget } from "react-icons/fi";
import { FaCoins } from "react-icons/fa";

function Badge({ children, className = "" }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs tracking-wide " +
        className
      }
    >
      {children}
    </span>
  );
}

const StatusIcon = ({ status }) => {
  switch (status) {
    case "Accepted":
      return (
        <span className="inline-flex items-center gap-1 text-emerald-300">
          <FiCheckCircle className="shrink-0" />
          <span className="text-xs">Completed</span>
        </span>
      );
    case "Attempted":
    case "Reset":
      return (
        <span className="inline-flex items-center gap-1 text-amber-300">
          <FiTarget className="shrink-0" />
          <span className="text-xs">
            {status === "Reset" ? "Attempted & Reset" : "Attempted"}
          </span>
        </span>
      );
    default:
      return null;
  }
};

function CopyButton({ code }) {
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(code);
    } catch {}
  };
  return (
    <button
      onClick={onCopy}
      className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] uppercase tracking-wider hover:bg-white/10"
      aria-label="Copy code"
      type="button"
    >
      Copy
    </button>
  );
}

function CompetitionMarkdown({ challenge }) {
  const { executionStatus, challengeProgress } = useSelector(
    (state) => state.competitions
  );
  const { user } = useSelector((state) => state.auth);

  const instanceUrl = user[0]?.instanceUrl;

  const ch = challenge?.[0];
  const status =
    executionStatus?.submissionResults?.status || challengeProgress?.status;

  const processedChallenge = useMemo(() => {
    let content = ch?.challenge || "";
    if (instanceUrl) {
      content = content.replace(
        /https:\/\/login.salesforce.com/g,
        instanceUrl
      );
    }
    return content;
  }, [ch?.challenge, instanceUrl]);


  const levelPill = useMemo(() => {
    if (!ch?.level) return null;
    return (
      <Badge className="border-[var(--advent-gold)]/50 bg-[color:var(--advent-gold)]/10 text-[color:var(--advent-gold)]">
        {ch.level}
      </Badge>
    );
  }, [ch?.level]);

  return (
    <div
      className="bg-[#FFF9E6] text-slate-100 h-full overflow-y-auto"
      id="challenge-panel"
    >
      <style jsx="true">{`
        @import url("https://fonts.googleapis.com/css2?family=Cabin+Sketch:wght@400;700&family=Fira+Code:wght@400;600&display=swap");

        :root {
          --advent-red: #d32f2f;
          --advent-green: #2e7d32;
          --advent-gold: #ffd700;
          --advent-light: #fff9e6;
          --advent-dark: #3e2723;
        }

        .advent-header {
          background: linear-gradient(
            to bottom,
            var(--advent-red),
            #a91d1d
          );
          border-bottom: 8px solid var(--advent-gold);
        }

        .advent-title {
          font-family: "Cabin Sketch", cursive;
          color: var(--advent-light);
          text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
        }

        .advent-sub {
          font-family: "Cabin Sketch", cursive;
          color: var(--advent-light);
        }

        /* Markdown theming */
        .md p {
          color: var(--advent-dark);
        }
        .md h2,
        .md h3,
        .md h4 {
          font-family: "Cabin Sketch", cursive;
          color: var(--advent-green);
        }
        .md a {
          color: var(--advent-red);
          text-decoration: underline;
          text-underline-offset: 4px;
        }
        .md blockquote {
          border-left: 4px solid var(--advent-green);
          background: rgba(46, 125, 50, 0.06);
          color: var(--advent-dark);
        }
        .md table {
          background: rgba(255, 249, 230, 0.5);
        }
        .md th {
          color: var(--advent-dark);
        }
        .md code:not([class*="language-"]) {
          font-family: "Fira Code", monospace;
          border: 1px solid rgba(62, 39, 35, 0.12);
          background: rgba(62, 39, 35, 0.05);
          color: var(--advent-dark);
        }
      `}</style>

      {/* ADVENT HEADER */}
      <div className="sticky top-0 z-10">
        <div className="advent-header relative px-8 pt-8 pb-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <h1 className="advent-title text-2xl md:text-3xl font-bold leading-tight">
                {ch?.name || "Challenge"}
              </h1>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm advent-sub">
                <StatusIcon status={status} />
                {typeof ch?.points === "number" && (
                  <Badge className="border-[var(--advent-gold)]/40 bg-[color:var(--advent-gold)]/20 text-[color:var(--advent-dark)]">
                    <FaCoins className="text-yellow-500" />
                    <span className="text-yellow-800 font-bold">{ch.points} pts</span>
                  </Badge>
                )}
                {levelPill}
                {ch?.tag && (
                  <Badge className="border-[var(--advent-red)]/50 bg-[color:var(--advent-red)]/10 text-[color:var(--advent-red)]">
                    #{ch.tag}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MARKDOWN CONTENT */}
      <div className="px-8 py-8 md:py-10 md:px-10 md:pr-8 md:pb-12 md:max-w-[1200px] md:mx-auto md:pl-10 text-gray-800">
        <div className="md">
          <ReactMarkdown
            children={processedChallenge}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ node, ...props }) => (
                <h2
                  className="mt-8 mb-3 text-2xl font-extrabold tracking-tight"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h3
                  className="mt-7 mb-3 text-xl font-bold tracking-tight"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h4
                  className="mt-6 mb-2 text-lg font-semibold tracking-tight"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p className="mb-4 leading-7" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="mb-4 list-disc pl-6 space-y-1" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="mb-4 list-decimal pl-6 space-y-1" {...props} />
              ),
              li: ({ node, ...props }) => <li className="leading-7" {...props} />,
              a: ({ node, ...props }) => (
                <a
                  className="underline decoration-red-600/50 underline-offset-4 hover:text-red-800"
                  target="_blank"
                  rel="noreferrer"
                  {...props}
                />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote
                  className="my-5 px-5 py-3 italic rounded-md"
                  {...props}
                />
              ),
              hr: () => (
                <div className="my-8 h-px w-full bg-gradient-to-r from-transparent via-gray-400/20 to-transparent" />
              ),
              table: ({ node, ...props }) => (
                <div className="my-6 overflow-x-auto rounded-lg border border-gray-400/20">
                  <table className="w-full border-collapse" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="bg-gray-100/70" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th
                  className="border-b border-gray-400/20 px-3 py-2 text-left"
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td className="px-3 py-2 align-top" {...props} />
              ),
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                const codeText = String(children).replace(/\n$/, "");
                if (!inline && match) {
                  return (
                    <div className="my-5 overflow-hidden rounded-lg border border-gray-400/20 bg-gray-900">
                      <div className="flex items-center justify-between border-b border-gray-400/20 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-300/80">
                        <span className="advent-sub text-gray-300">{match[1]}</span>
                        <CopyButton code={codeText} />
                      </div>
                      <SyntaxHighlighter
                        children={codeText}
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{ margin: 0, background: "transparent" }}
                        {...props}
                      />
                    </div>
                  );
                }
                return (
                  <code
                    className={
                      "rounded-md px-1.5 py-0.5 text-[13px] " +
                      (className || "")
                    }
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default CompetitionMarkdown;